// User Store migrated from @es-bill/es-bill-common-components for local control and extensibility.
// Only uses data from the token for now. Extend as needed for backend/user profile integration.
import { defineStore } from 'pinia';
import { jwtDecode } from 'jwt-decode';
import { inject } from 'vue';
// Optionally define a DecodedToken type/interface if you want type safety
// import { DecodedToken } from '@/interfaces/DecodedToken';

export const useUserStore = defineStore('userStore', {
  state: () => ({
    token: null, // string | null
    roles: [], // string[]
    communities: [], // string[]
    meteringpoints: [], // string[]
    givenName: '', // string
    familyName: '', // string
    email: '', // string
    userId: '', // string (sub)
    sessionId: '', // string (sid or jti)
  }),
  actions: {
    /**
     * Set the token and update roles and user info
     * @param token The token to set
     * @param log Logger instance (default is the injected log)
     * @throws Error if the token is invalid
     */
    setToken(token, log = inject('log')) {
      this.token = token;
      try {
        const decoded = jwtDecode(token);
        this.roles = decoded.realm_access?.roles || [];
        this.communities = decoded['communities-operator'] || [];
        this.meteringpoints = decoded['meteringpoints-operator'] || [];
        // Handle overflow of communities and meteringpoints
        for (let i = 1; i < 10; i++) {
          if (decoded['communities-operator' + i]) {
            this.communities = this.communities.concat(decoded['communities-operator' + i]);
          }
        }
        for (let i = 1; i < 10; i++) {
          if (decoded['meteringpoints-operator' + i]) {
            this.meteringpoints = this.meteringpoints.concat(decoded['meteringpoints-operator' + i]);
          }
        }
        // User identity fields
        this.givenName = decoded.given_name || '';
        this.familyName = decoded.family_name || '';
        this.email = decoded.email || '';
        this.userId = decoded.sub || '';
        this.sessionId = decoded.sid || decoded.jti || '';
      } catch (error) {
        console.error('Failed to decode token:', error);
        this.token = null;
        this.roles = [];
        this.communities = [];
        this.meteringpoints = [];
        this.givenName = '';
        this.familyName = '';
        this.email = '';
        this.userId = '';
        this.sessionId = '';
        throw new Error('Failed to decode token: ' + error);
      }
    },
    logout() {
      this.token = null;
      this.roles = [];
      this.communities = [];
      this.meteringpoints = [];
      this.givenName = '';
      this.familyName = '';
      this.email = '';
      this.userId = '';
      this.sessionId = '';
    }
  },
  getters: {
    isAuthenticated: (state) => !!state.token,
    userRoles: (state) => state.roles,
    hasCommunityAccess: (state) => (communityId) => state.communities.includes(communityId),
    hasMeteringPointAccess: (state) => (meteringPointId) => state.meteringpoints.includes(meteringPointId),
    atLeastMaintainerOrAdmin: (state) => () => state.roles.includes('es-bill-admin') || state.communities.length > 0,
    isAdmin: (state) => () => state.roles.includes('es-bill-admin'),
    fullName: (state) => (state.givenName + ' ' + state.familyName).trim(),
    bearerToken: (state) => state.token ? `Bearer ${ state.token }` : '',
  }
});
